import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createGtag } from "vue-gtag";
import { createHead } from '@vueuse/head'

import { initializeStores } from './stores'
import './assets/css/tailwind.css'
import referralCodeHandler from './plugins/referralCodeHandler'
import updateChecker, { isReloadingForUpdate } from './plugins/updateChecker'
import lazyLoadDirective from './directives/lazyLoad'

import App from './App.vue'
import router from './router'
import brand, { brandPlugin, hasFeature } from './brand'

import * as Sentry from "@sentry/vue";

const app = createApp(App)
const head = createHead()
const apiUrl = import.meta.env.VITE_API_BASE_URL || brand.apiUrl

// Everything goes to Sentry: errors, every console level as logs, traces with Vue component spans,
// a JS profile of every traced page load and navigation, and a replay of every visit
if (brand.sentry?.dsn) Sentry.init({
  app,
  dsn: brand.sentry.dsn,
  // Sent through the shop API: blockers (EasyPrivacy in uBlock Origin, Brave) drop requests to Sentry
  tunnel: `${apiUrl}/diagnostics`,
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      // The page as the customer sees it, typed text included (password fields stay masked)
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      // Short visits too (by default replays under 5 s are dropped)
      minReplayDuration: 0,
      // Request and response bodies of every API call
      networkDetailAllowUrls: [`${apiUrl}/`],
      networkResponseHeaders: ['X-Correlation-ID'],
    }),
    // Page loads and navigations named after the route
    Sentry.browserTracingIntegration({ router }),
    // Needs the Document-Policy: js-profiling header on index.html (docker/nginx.conf, vite.config.js);
    // browsers without the JS Self-Profiling API (Safari, Firefox) just send no profiles
    Sentry.browserProfilingIntegration(),
    Sentry.consoleLoggingIntegration(),
  ],
  tracingOptions: { trackComponents: true },
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 1.0,
  profileSessionSampleRate: 1.0,
  // Profile while a page load or navigation span runs (the default, 'manual', waits for uiProfiler.startProfiler())
  profileLifecycle: 'trace',
  enableLogs: true,
  // Chunks of the old build failing after a deploy: the page is already reloading at the new one
  beforeSend: (event) => (isReloadingForUpdate() ? null : event),
});

// iOS still turns two quick taps into its double-tap gesture (zoom, or a scroll to the tapped text as
// the page can't zoom) despite touch-action: manipulation in tailwind.css. The second of two taps within
// 300 ms loses the gesture but keeps its click, so e.g. a quantity + button still counts every tap.
// Swipes don't count as taps; form fields keep their own double tap (word selection)
let tapStart = null
let lastTapEnd = 0
document.addEventListener('touchstart', (event) => {
  const touch = event.touches[0]
  tapStart = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null
}, { passive: true })
document.addEventListener('touchend', (event) => {
  const touch = event.changedTouches[0]
  const isTap = tapStart && touch && Math.hypot(touch.clientX - tapStart.x, touch.clientY - tapStart.y) < 10
  tapStart = null
  if (!isTap) return
  const isSecondTap = event.timeStamp - lastTapEnd < 300
  lastTapEnd = event.timeStamp
  if (!isSecondTap || !event.cancelable || event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return
  event.preventDefault()
  event.target.dispatchEvent(new MouseEvent('click', {
    bubbles: true, cancelable: true, view: window, clientX: touch.clientX, clientY: touch.clientY,
  }))
}, { passive: false })

app.directive('lazy', lazyLoadDirective)

// Before the router: its first navigation already loads chunks
app.use(updateChecker, { router })
app.use(createPinia())
app.use(head)
app.use(router)
app.use(brandPlugin)
if (brand.analytics?.gtagId) app.use(createGtag({ tagId: brand.analytics.gtagId }))
if (hasFeature('referrals')) app.use(referralCodeHandler)

initializeStores()
app.mount('#app')